const DEFAULT_MIN_DURATION_SEC = 0.02;

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function cloneTimedWords(timedLyrics = []) {
  return (timedLyrics || []).map((w) => ({ ...w }));
}

function byWordOrder(a, b) {
  const la = safeNum(a.lineIndex, 0);
  const lb = safeNum(b.lineIndex, 0);
  if (la !== lb) return la - lb;
  const wa = safeNum(a.wordIndex, 0);
  const wb = safeNum(b.wordIndex, 0);
  return wa - wb;
}

export function applyWordTimingEdit(timedLyrics = [], edit = {}) {
  const out = cloneTimedWords(timedLyrics);
  const diagnostics = {
    shiftedCount: 0,
    totalShiftSec: 0,
    clampedStart: false,
    clampedEnd: false,
    clampedToPrevious: false,
    targetFound: false,
    mode: edit?.mode || 'both',
  };

  if (!out.length) {
    return { timedLyrics: out, diagnostics, error: 'No timed lyrics available' };
  }

  const targetLine = Number(edit?.lineIndex);
  const targetWord = Number(edit?.wordIndex);
  if (!Number.isFinite(targetLine) || !Number.isFinite(targetWord)) {
    return { timedLyrics: out, diagnostics, error: 'Invalid target word reference' };
  }

  const minDuration = Math.max(0.001, safeNum(edit?.minDurationSec, DEFAULT_MIN_DURATION_SEC));
  const mode = String(edit?.mode || 'both');

  const targetIdx = out.findIndex(
    (w) => Number(w?.lineIndex) === targetLine && Number(w?.wordIndex) === targetWord,
  );
  if (targetIdx < 0) {
    return { timedLyrics: out, diagnostics, error: 'Target word not found in timed lyrics' };
  }
  diagnostics.targetFound = true;

  const target = out[targetIdx];
  const originalStart = safeNum(target.start);
  const originalEnd = Math.max(originalStart, safeNum(target.end, originalStart));

  let nextStart = originalStart;
  let nextEnd = originalEnd;

  if (mode === 'start' || mode === 'both') {
    if (Number.isFinite(Number(edit?.newStart))) {
      nextStart = Math.max(0, Number(edit.newStart));
      if (nextStart !== Number(edit.newStart)) diagnostics.clampedStart = true;
    }
  }
  if (mode === 'end' || mode === 'both') {
    if (Number.isFinite(Number(edit?.newEnd))) {
      nextEnd = Math.max(0, Number(edit.newEnd));
      if (nextEnd !== Number(edit.newEnd)) diagnostics.clampedEnd = true;
    }
  }

  const lineWords = out
    .map((w, idx) => ({ ...w, _idx: idx }))
    .filter((w) => Number(w.lineIndex) === targetLine)
    .sort(byWordOrder);

  const targetLinePos = lineWords.findIndex((w) => Number(w.wordIndex) === targetWord);
  const prevOnLine = targetLinePos > 0 ? lineWords[targetLinePos - 1] : null;

  if (prevOnLine) {
    const prevEnd = Math.max(safeNum(prevOnLine.start), safeNum(prevOnLine.end, prevOnLine.start));
    if (nextStart < prevEnd) {
      diagnostics.clampedToPrevious = true;
      nextStart = prevEnd;
    }
  }

  if (nextEnd < nextStart + minDuration) {
    nextEnd = nextStart + minDuration;
    diagnostics.clampedEnd = true;
  }

  target.start = Number(nextStart.toFixed(3));
  target.end = Number(nextEnd.toFixed(3));

  // First cascade in-line so same-line words remain monotonic.
  let runningEnd = target.end;
  for (let i = targetLinePos + 1; i < lineWords.length; i += 1) {
    const wi = lineWords[i]._idx;
    const word = out[wi];
    const curStart = safeNum(word.start);
    const curEnd = Math.max(curStart, safeNum(word.end, curStart));
    if (curStart < runningEnd) {
      const shift = runningEnd - curStart;
      const dur = Math.max(minDuration, curEnd - curStart);
      word.start = Number((curStart + shift).toFixed(3));
      word.end = Number((word.start + dur).toFixed(3));
      diagnostics.shiftedCount += 1;
      diagnostics.totalShiftSec += shift;
    }
    runningEnd = Math.max(safeNum(word.start), safeNum(word.end, word.start));
  }

  // Then cascade globally by original lyric order to prevent cross-line overlaps.
  for (let i = Math.max(1, targetIdx + 1); i < out.length; i += 1) {
    const prev = out[i - 1];
    const cur = out[i];
    const prevEnd = Math.max(safeNum(prev.start), safeNum(prev.end, prev.start));
    const curStart = safeNum(cur.start);
    const curEnd = Math.max(curStart, safeNum(cur.end, curStart));
    if (curStart < prevEnd) {
      const shift = prevEnd - curStart;
      const dur = Math.max(minDuration, curEnd - curStart);
      cur.start = Number((curStart + shift).toFixed(3));
      cur.end = Number((cur.start + dur).toFixed(3));
      diagnostics.shiftedCount += 1;
      diagnostics.totalShiftSec += shift;
    }
  }

  diagnostics.totalShiftSec = Number(diagnostics.totalShiftSec.toFixed(3));
  return { timedLyrics: out, diagnostics, error: null };
}

export function buildTimingEditDiagnostics(beforeTimed = [], afterTimed = [], target = null) {
  const beforeMap = new Map(
    (beforeTimed || []).map((w) => [`${Number(w.lineIndex)}:${Number(w.wordIndex)}`, w]),
  );
  let changedCount = 0;
  let totalDeltaSec = 0;

  (afterTimed || []).forEach((w) => {
    const key = `${Number(w.lineIndex)}:${Number(w.wordIndex)}`;
    const prev = beforeMap.get(key);
    if (!prev) return;
    const ds = Math.abs(safeNum(w.start) - safeNum(prev.start));
    const de = Math.abs(safeNum(w.end) - Math.max(safeNum(prev.start), safeNum(prev.end, prev.start)));
    if (ds > 0.0005 || de > 0.0005) {
      changedCount += 1;
      totalDeltaSec += ds + de;
    }
  });

  return {
    target: target || null,
    changedCount,
    totalDeltaSec: Number(totalDeltaSec.toFixed(3)),
  };
}
